import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Grid2 as Grid,
    Typography,
    useTheme,
} from "@mui/material";
import {
    ArrowForwardRounded,
    BusinessRounded,
} from "@mui/icons-material";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { sagaActions } from "../../helper/redux/actions/SagaActions";
import { media_AddImage, media_getCatById } from "../../network/api/multimedia.api";
import { corporate_Update, corporate_UpdateLogo } from "../../network/api/corporate.api";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { resizeCanvas } from "../../helper/utils";

import _CorporateLogoCropModal from "./partials/_CorporateLogoCropModal";
import _CorporateLogoCard from "./partials/_CorporateLogoCard";
import _CorporateForm from "./partials/_CorporateForm";

const EditCorporate = (props) => {
    const theme = useTheme();
    const error = useContext(ErrorContext);
    const navigate = useNavigate();
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const [imageUrl, setImageUrl] = useState("");
    const [inCorporate, setInCorporate] = useState(corporate || {});
    const [imageToCrop, setImageToCrop] = useState(null);
    const [ratio, setRatio] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const cropperRef = useRef(null);

    useEffect(() => {
        setImageUrl(corporate?.Logo?.Url || "");
        setInCorporate(corporate || {});

        try {
            if (corporate?.Address && corporate?.Email && corporate?.Tel && corporate?.Name) {
                props?.introCanGoNext?.(true);
            }
        } catch (e) { }
    }, [corporate]);

    useEffect(() => {
        document.title = 'ویرایش مشخصات سازمان | جیم‌پین سازمانی';
        props.RequestCorporate(corporate);
        getRatio();
    }, []);

    function getRatio() {
        media_getCatById({ id: 5 })
            .then(result => {
                setRatio(result.data.Data);
            })
            .catch(e => {
                const msg = e?.response?.data?.Message || "خطا در دریافت ابعاد لوگو";
                error.showError({ message: msg });
            });
    }

    function updateCorporate(e) {
        if (e) e.preventDefault();

        if (!inCorporate?.Name?.trim()) {
            error.showError({ message: "لطفاً نام سازمان را وارد نمایید." });
            return;
        }

        setSaving(true);
        corporate_Update({
            Id: corporate.Id,
            Name: inCorporate.Name.trim(),
            Address: inCorporate.Address || "",
            Email: inCorporate.Email || "",
            Tel: inCorporate.Tel || "",
        })
            .then(() => {
                props.RequestCorporate(corporate);
                error.showError({ message: "مشخصات سازمان با موفقیت به‌روزرسانی شد." });
            })
            .catch(e => {
                const msg = e?.response?.data?.Message || "خطا در ذخیره مشخصات سازمان";
                error.showError({ message: msg });
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function uploadImage() {
        let canvas = cropperRef.current?.getCanvas();
        if (canvas) {
            if (canvas.height < (ratio?.MINH || 200)) {
                error.showError({ message: "ابعاد تصویر لوگو کوچک است." });
                return;
            }
            if (canvas.height > (ratio?.MAXH || 2000)) {
                canvas = resizeCanvas(canvas, ratio?.MAXH || 2000, null);
            }

            setUploadingLogo(true);
            canvas.toBlob((blob) => {
                if (blob) {
                    setImageToCrop(null);
                    const formData = new FormData();
                    formData.append("MediaType", "IMAGE");
                    formData.append("File", blob);
                    formData.append("CategoryId", ratio?.Id || "5");
                    formData.append("Title", corporate?.Name || "logo");
                    formData.append("Description", corporate?.Id || "corporate");

                    media_AddImage(formData)
                        .then(data => {
                            corporate_UpdateLogo({
                                CorporateId: corporate.Id,
                                MultimediaId: data.data.Data.Id,
                            })
                                .then(result => {
                                    props.RequestCorporate(corporate);
                                    setImageUrl(result.data.Data.Avatar ? (result.data.Data.Avatar.Url + "&width=200") : "");
                                    error.showError({ message: "لوگوی سازمان با موفقیت ثبت شد." });
                                })
                                .catch(e => {
                                    const msg = e?.response?.data?.Message || "خطا در تنظیم لوگو";
                                    error.showError({ message: msg });
                                });
                        })
                        .catch(e => {
                            const msg = e?.response?.data?.Message || "خطا در بارگذاری تصویر";
                            error.showError({ message: msg });
                        })
                        .finally(() => {
                            setUploadingLogo(false);
                        });
                } else {
                    setUploadingLogo(false);
                }
            });
        }
    }

    const handleSelectImage = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onload = () => {
                setImageToCrop(reader.result);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Executive Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 3.5,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: '#fff',
                            boxShadow: '0 8px 20px rgba(45, 107, 226, 0.3)',
                        }}
                    >
                        <BusinessRounded sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                            مشخصات و برندینگ سازمان
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            تنظیم نام رسمی، اطلاعات تماس، آدرس پستی و لوگوی نمایش‌داده‌شده برای پرسنل
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<ArrowForwardRounded />}
                    onClick={() => navigate('/settings/corporates')}
                    sx={{ borderRadius: 3, fontWeight: 700 }}
                >
                    لیست سازمان‌ها
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Logo Column */}
                <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                    <_CorporateLogoCard
                        imageUrl={imageUrl}
                        inCorporate={inCorporate}
                        corporateId={corporate?.Id}
                        uploadingLogo={uploadingLogo}
                        onSelectImage={handleSelectImage}
                    />
                </Grid>

                {/* Form Column */}
                <Grid size={{ xs: 12, md: 8, lg: 9 }}>
                    <_CorporateForm
                        inCorporate={inCorporate}
                        setInCorporate={setInCorporate}
                        saving={saving}
                        onSave={updateCorporate}
                    />
                </Grid>
            </Grid>

            <_CorporateLogoCropModal
                open={!!imageToCrop}
                onClose={() => setImageToCrop(null)}
                imageToCrop={imageToCrop}
                cropperRef={cropperRef}
                ratio={ratio}
                onConfirmUpload={uploadImage}
            />
        </Container>
    );
};

export default connect(null, sagaActions)(EditCorporate);
